import { $, $$, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { fillForm } from '../lib/form.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toast } from '../lib/toast.js';

/**
 * The Modmail page.
 *
 * One form for the settings, and therefore one save bar. Unblocking escapes it
 * on purpose: it is not a setting but an act, taking effect the moment it is
 * clicked, and putting it behind "Save" would suggest it could be taken back.
 */

interface Settings {
  channelId: string;
  staffRoleIds: string[];
  mentionOnOpen: boolean;
  welcomeMessage: string;
  closeMessage: string;
  anonymous: boolean;
  noteSign: string;
  blockedUserIds: string[];
}

export function initModmailPage(): void {
  const form = $<HTMLFormElement>('[data-modmail-form]');
  if (!form) return;

  const { settings, guildId } = pageData<{ settings: Settings; guildId: string }>();

  /*
   * The save bar is armed **first**, before any widget is mounted.
   *
   * Saving is the essential function of a settings page; it must not depend on
   * a select menu that can throw (AGENTS §3.5).
   */
  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/modmail?guildId=${guildId}`, {
        guildId,
        channelId: findSelectMenu(form, 'channelId')?.single ?? '',
        staffRoleIds: findSelectMenu(form, 'staffRoleIds')?.value ?? [],
        mentionOnOpen: checkbox(form, 'mentionOnOpen'),
        welcomeMessage: text(form, 'welcomeMessage'),
        closeMessage: text(form, 'closeMessage'),
        anonymous: checkbox(form, 'anonymous'),
        noteSign: text(form, 'noteSign'),
      });
    },
  });

  fillForm(form, settings as unknown as Record<string, unknown>);

  const channel = findSelectMenu(form, 'channelId');
  if (channel) channel.value = settings.channelId ? [settings.channelId] : [];

  const roles = findSelectMenu(form, 'staffRoleIds');
  if (roles) roles.value = settings.staffRoleIds;

  wireUnblock(form, guildId);
}

/** Unblocking: an act, not a setting. */
function wireUnblock(form: HTMLFormElement, guildId: string): void {
  for (const button of $$<HTMLButtonElement>('[data-unblock]', form)) {
    button.addEventListener('click', () => {
      const userId = button.dataset.unblock ?? '';
      if (!userId) return;

      void api
        .post(`/api/modmail/block?guildId=${guildId}`, { guildId, userId, blocked: false })
        .then(() => {
          // The row goes as soon as the server confirms: leaving it would make
          // the page disagree with what the bot now does.
          button.closest('li')?.remove();
          toast(ct('modmailUnblocked', 'Member unblocked.'));
        });
    });
  }
}

function text(form: HTMLFormElement, name: string): string {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement
    ? field.value.trim()
    : '';
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
